import { useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client.js';
import { LANGUAGES, setLanguage, toLanguage, type Language } from '../i18n.js';
import { ME_KEY } from '../session.js';

/** Short labels as on the landing page and in the mock-ups (DE · EN · UK). */
const SHORT: Record<Language, string> = { de: 'DE', en: 'EN', uk: 'UK' };

/**
 * The UI language and the way to change it: signed-in users keep it for the app and – until
 * chosen separately – for their e-mails.
 */
export function useLanguage(signedIn: boolean) {
  const { i18n } = useTranslation();
  const client = useQueryClient();
  const current = toLanguage(i18n.language);
  const change = async (language: Language) => {
    if (language === current) return;
    setLanguage(language);
    if (signedIn) {
      await api.PATCH('/me', { body: { locale: language } });
      await client.invalidateQueries({ queryKey: ME_KEY });
    }
  };
  return { current, change };
}

/**
 * The three language buttons (sheet: .seg); `t` gives the group's label in the page's language
 * (the public status page shows the account's language, not the app's).
 */
export function LanguageButtons({
  current,
  onChange,
  t,
  className = '',
}: {
  current: Language;
  onChange: (language: Language) => void;
  t: TFunction;
  className?: string;
}) {
  return (
    <div role="group" aria-label={t('nav.language')} className={`seg ${className}`}>
      {LANGUAGES.map((l) => (
        <button
          key={l}
          type="button"
          lang={l}
          aria-pressed={l === current}
          // The name is the visible «UK» (WCAG 2.5.3: speech input says what it sees, as on the
          // landing); the language in full is the description and the tooltip (SP-A2.84).
          title={t(`lang.${l}`)}
          onClick={() => onChange(l)}
        >
          {SHORT[l]}
        </button>
      ))}
    </div>
  );
}

/** Changes the UI language (T-41: three buttons instead of a drop-down; sheet: .seg, SP-UX10.2). */
export function LanguageSwitch({
  signedIn,
  className = '',
}: {
  signedIn: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const { current, change } = useLanguage(signedIn);
  return (
    <LanguageButtons
      current={current}
      onChange={(l) => void change(l)}
      t={t}
      className={className}
    />
  );
}
